// ---------------------------------------------------------
// Datei: PhaseFooterNav.tsx
// Zweck: Direkter Phasen-Wechsel aus einer aktiven Phase (Issue #16)
// Sitzt visuell über der globalen BottomNavigation und zeigt
// alle Phasen des aktuellen Projekts als Icon + Kurz-Label.
// ---------------------------------------------------------

import React from 'react';
import { useNavigate } from 'react-router-dom';
import PhaseIcon, { getPhaseKey, PHASE_SHORT_LABELS } from './phaseIcons';

interface PhaseLike {
  _id: string;
  name?: string;
}

interface PhaseFooterNavProps {
  phases: PhaseLike[];
  currentPhaseId: string;
  projectId: string;
}

const PhaseFooterNav: React.FC<PhaseFooterNavProps> = ({ phases, currentPhaseId, projectId }) => {
  const navigate = useNavigate();

  if (!phases || phases.length === 0 || !projectId) {
    return null;
  }

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg pb-[env(safe-area-inset-bottom)]"
      data-testid="phase-footer-nav"
      aria-label="Phasen-Navigation"
    >
      <div className="max-w-screen-xl mx-auto">
        <div
          className="grid h-20"
          style={{ gridTemplateColumns: `repeat(${phases.length}, minmax(0, 1fr))` }}
        >
          {phases.map((phase) => {
            const isActive = phase._id === currentPhaseId;
            const key = getPhaseKey(phase);
            const label = PHASE_SHORT_LABELS[key];

            return (
              <button
                key={phase._id}
                type="button"
                data-testid={`phase-footer-nav-${key}`}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => {
                  if (isActive) return;
                  // replace:true – Phasen sind gleichwertige Tabs (kein History-Stack).
                  navigate(`/projects/${projectId}/phase/${phase._id}`, { replace: true });
                }}
                className={`
                  flex flex-col items-center justify-center px-1 gap-1
                  mx-1 my-1.5 rounded-xl
                  transition-all duration-150
                  ${isActive
                    ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}
                  active:scale-95
                  focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-inset
                `}
                title={phase.name || label}
              >
                <PhaseIcon phase={phase} className="w-6 h-6" />
                <span
                  lang="de"
                  className={`text-[10px] leading-tight font-medium text-center break-words hyphens-auto max-w-full ${isActive ? 'font-semibold' : ''}`}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default PhaseFooterNav;
